import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from './ui/button';

import { BASE_URL as API } from '../App';

// Current frontend version - MUST match backend APP_VERSION when deployed
const FRONTEND_VERSION = "3.6.0";

// Check interval in milliseconds (30 seconds for testing, increase for production)
const CHECK_INTERVAL = 30000;

export default function VersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const checkVersion = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/version`);
      if (response.ok) {
        const data = await response.json();
        setServerVersion(data.version);
        
        // Compare versions
        if (data.version !== FRONTEND_VERSION) {
          console.log(`Version mismatch: Frontend ${FRONTEND_VERSION}, Server ${data.version}`);
          setUpdateAvailable(true);
        } else {
          setUpdateAvailable(false);
        }
      }
    } catch (error) {
      console.error('Version check failed:', error);
    }
  }, []);

  useEffect(() => {
    // Check immediately on mount
    checkVersion();

    // Then check periodically
    const interval = setInterval(checkVersion, CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [checkVersion]);

  const handleRefresh = async () => {
    try {
      // Tell service worker to clear cache
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage('clearCache');
      }
      
      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
      }
      
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Clear localStorage version info
      localStorage.removeItem('appVersion');
      
      // Small delay to ensure cleanup completes
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force reload from server (bypass all caches)
      window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now();
    } catch (error) {
      console.error('Error during refresh:', error);
      // Fallback: just reload
      window.location.reload(true);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Reset dismissed after 5 minutes so it shows again
    setTimeout(() => setDismissed(false), 5 * 60 * 1000);
  };

  if (!updateAvailable || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-blue-600 text-white rounded-lg shadow-lg p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <RefreshCw className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Update Available</p>
              <p className="text-sm text-blue-100 mt-1">
                A new version ({serverVersion}) is available. You&apos;re on {FRONTEND_VERSION}.
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-blue-200 hover:text-white p-1"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <Button
            onClick={handleRefresh}
            className="flex-1 bg-white text-blue-600 hover:bg-blue-50"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Update Now
          </Button>
          <Button
            onClick={handleDismiss}
            variant="ghost"
            className="text-blue-100 hover:text-white hover:bg-blue-700"
            size="sm"
          >
            Later
          </Button>
        </div>
      </div>
    </div>
  );
}

// Export version for other components to use
export { FRONTEND_VERSION };
